<script setup lang="ts">
import { computed } from 'vue'
import Tooltip from './Tooltip.vue'
import { useDisplay } from 'vuetify'

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    top?: boolean
    bottom?: boolean
    text?: string
    tooltip?: boolean
    icon?: string
    highlightColor?: string
    errorText?: string
    custom?: boolean
    compact?: boolean
    customFields?: boolean
    contentGapPx?: number
    mobileBorder?: boolean
  }>(),
  {
    title: '',
    subtitle: '',
    top: false,
    bottom: false,
    text: '',
    tooltip: false,
    icon: '',
    highlightColor: '',
    errorText: '',
    custom: false,
    compact: false,
    customFields: false,
    contentGapPx: 16,
    mobileBorder: false,
  }
)

defineEmits<{
  (e: 'clickIcon'): void
}>()

const isBottom = computed<boolean>(() => props.bottom)

const cardBorderClass = computed(() => {
  if (props.highlightColor === 'error') return 'border-error'
  if (props.highlightColor === 'success') return 'border-success'
  return 'border-grey-lighten-2'
})

const cardPaddingClass = computed(() => {
  if (props.title) {
    if (props.compact) return 'pa-4'
    return isMobile.value ? 'pa-4' : 'pa-6'
  }
  return isMobile.value ? 'px-4' : 'px-6'
})

const isMdiIcon = computed(() =>
  props.icon ? props.icon.startsWith('mdi-') : false
)

const contentGapStyle = computed(() => {
  const v = props.contentGapPx
  const safe = typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 16
  return { marginTop: `${safe}px` }
})

const computedBorderWidth = computed(() => {
  if (!isMobile.value) return '1px'
  return props.mobileBorder ? '1px' : '0px'
})
</script>

<template>
  <div
    class="w-100"
    :class="[
      isMobile
        ? props.top
          ? 'mb-4'
          : isBottom
            ? 'mb-8 mt-4'
            : props.custom
              ? 'mt-4'
              : 'my-4'
        : props.top
          ? 'mb-6'
          : isBottom
            ? 'mb-16 mt-6'
            : props.custom
              ? 'mt-6'
              : 'my-6',
    ]"
  >
    <component
      :is="isMobile ? 'div' : 'v-card'"
      flat
      class="section-card d-flex flex-column align-start ga-2"
      :class="[cardBorderClass, cardPaddingClass]"
      :style="{
        borderWidth: computedBorderWidth,
        borderStyle: 'solid',
        borderRadius: '16px',
      }"
    >
      <!-- HEADER -->
      <div
        class="section-header d-flex align-center w-100 justify-space-between"
      >
        <!-- LEFT -->
        <div
          class="d-flex align-center justify-space-between w-100 flex-grow-1"
          style="min-width: 0"
        >
          <p v-if="props.title" class="section-title mb-0">
            {{ props.title }}
          </p>

          <slot name="info" />

          <Tooltip v-if="props.icon" :header-text="props.text">
            <button
              type="button"
              class="section-tooltip-button"
              :class="props.customFields ? 'mt-2' : 'mr-n3 pt-2'"
              @click="$emit('clickIcon')"
            >
              <v-icon
                v-if="isMdiIcon"
                :icon="props.icon"
                :size="props.customFields ? 16 : 20"
                class="text-grey-darken-1"
              />
              <img
                v-else
                :src="props.icon"
                alt=""
                :width="!props.customFields ? '20px' : '16px'"
                :height="!props.customFields ? '20px' : '16px'"
              />
            </button>
          </Tooltip>

          <Tooltip
            v-else-if="props.text"
            :header-text="props.text"
            class="text-body-2 font-weight-light text-grey-darken-1"
            position="right-0 top-full"
          >
            <p class="mb-0">{{ props.subtitle }}</p>
          </Tooltip>

          <p
            v-else-if="props.subtitle"
            class="text-body-2 font-weight-light text-grey-darken-1 mb-0"
          >
            {{ props.subtitle }}
          </p>
        </div>

        <!-- RIGHT -->
        <div class="d-flex align-center ga-2 ml-4">
          <slot name="append" />
        </div>
      </div>

      <!-- CONTENT -->
      <div class="section-content w-100" :style="contentGapStyle">
        <slot />
      </div>

      <p
        v-if="props.highlightColor === 'error' && props.errorText"
        class="text-caption d-flex align-center mt-4 text-error"
      >
        <v-icon
          icon="mdi-alert-circle-outline"
          size="18"
          class="mr-2 text-error"
        />
        {{ props.errorText }}
      </p>
    </component>

    <v-divider
      v-if="isMobile && props.custom && !isBottom"
      :thickness="1"
      class="mb-n4 mx-4 border-opacity-100"
    />
  </div>
</template>

<style scoped>
.border-error {
  border-color: var(--v-theme-error);
}
.border-success {
  border-color: var(--v-theme-success);
}
.border-grey-lighten-2 {
  border-color: #e0e0e0;
}

.section-header {
  margin-bottom: 0;
}

.section-content {
  min-width: 0;
}

.section-title {
  color: var(--opacity-text--primary, rgba(0, 0, 0, 0.87));

  /* h6 (title)/h6, regular */
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-35, 20px);
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
  letter-spacing: 0.05px;
}

.section-tooltip-button {
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

@media (max-width: 959.98px) {
  .section-title {
    color: var(--opacity-text--primary, rgba(0, 0, 0, 0.87));
    font-family: 'Open Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.5px;
  }
}
</style>
